import React, { useEffect, useMemo } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Typography, useTheme } from "@mui/material";
import Button from "@components/Button/Button";
import FormItem from "@components/FormItem/FormItem";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import {
  mapResourcesToOptions,
  mapResourceTypesToOptions,
  type Resource,
  type ResourceType,
} from "@features/vessel-call/vessel-call-status/MovementServices/form/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { toMinute } from "@utils/date-time";
import type { TFunction } from "i18next";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { Resource as ResourceInterface } from "../CloseMovementService";

interface EditResourceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (resource: ResourceInterface) => void;
  onRemove?: () => void;
  resource: ResourceInterface;
  resourceTypes: ResourceType[];
  resources: Resource[];
  showRemoveButton?: boolean;
  validateStartAfterAts?: boolean;
  ats?: string | Date | null;
  onlyResourceFieldsRequired?: boolean;
}

type ResourceFormData = {
  resourceType: string;
  resourceName?: string;
  numberOfResources: string;
  startTimeOfUsage?: Date | null;
  endTimeOfUsage?: Date | null;
  connectionTime?: Date | null;
  location?: string;
};

export const parseResourceDate = (value?: string | Date | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  const match = /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})$/.exec(value);
  if (!match) {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  const [, day, month, year, hours, minutes] = match;
  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
  );

  const isValid =
    date.getFullYear() === Number(year) &&
    date.getMonth() === Number(month) - 1 &&
    date.getDate() === Number(day) &&
    date.getHours() === Number(hours) &&
    date.getMinutes() === Number(minutes);

  return isValid ? date : null;
};

const getResourceSchema = (
  t: TFunction,
  ats?: Date | null,
  validateStartAfterAts?: boolean,
  onlyResourceFieldsRequired = false,
) =>
  z
    .object({
      resourceType: z.string().min(
        1,
        t("form-validation.required", {
          label: t("vessel-call.movement-services.close-form.fields.resource-type"),
        }),
      ),
      resourceName:
        validateStartAfterAts || onlyResourceFieldsRequired
          ? z.string().optional()
          : z.string().min(
              1,
              t("form-validation.required", {
                label: t("vessel-call.movement-services.close-form.fields.resource-name"),
              }),
            ),
      numberOfResources: z
        .string()
        .min(
          1,
          t("form-validation.required", {
            label: t("vessel-call.movement-services.close-form.fields.number-of-resources"),
          }),
        )
        .max(
          4,
          t("form-validation.must-have-maximum-digits", {
            label: t("vessel-call.movement-services.close-form.fields.number-of-resources"),
            max: 4,
          }),
        )
        .superRefine((val, ctx) => {
          if (!val || val.trim() === "") {
            return;
          }

          const label = t("vessel-call.movement-services.close-form.fields.number-of-resources");
          const trimmedVal = val.trim();

          if (trimmedVal.includes(".") || trimmedVal.includes(",")) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${label} must be an integer (no decimals).`,
            });
            return;
          }

          const num = parseInt(trimmedVal, 10);
          const isInteger = !isNaN(num) && num.toString() === trimmedVal;

          if (!isInteger) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${label} must be an integer.`,
            });
            return;
          }

          if (num < 1) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${label} must be a positive number.`,
            });
            return;
          }

          if (trimmedVal.length > 4) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t("form-validation.must-have-maximum-digits", {
                label,
                max: 4,
              }),
            });
          }
        }),
      startTimeOfUsage: onlyResourceFieldsRequired
        ? z.date().nullable().optional()
        : z
            .date()
            .nullable()
            .refine((val: Date | null) => val !== null, {
              message: t("form-validation.required", {
                label: t("vessel-call.movement-services.close-form.fields.start-time"),
              }),
            }),
      endTimeOfUsage: onlyResourceFieldsRequired
        ? z.date().nullable().optional()
        : z
            .date()
            .nullable()
            .refine((val: Date | null) => val !== null, {
              message: t("form-validation.required", {
                label: t("vessel-call.movement-services.close-form.fields.end-time"),
              }),
            }),
      connectionTime: z.date().nullable().optional(),
      location: z.string().optional(),
    })
    .refine(
      (data: { startTimeOfUsage?: Date | null; endTimeOfUsage?: Date | null }) => {
        if (!data.endTimeOfUsage || !data.startTimeOfUsage) return true;
        return data.endTimeOfUsage > data.startTimeOfUsage;
      },
      {
        message: t("form-validation.end-time-after-start"),
        path: ["endTimeOfUsage"],
      },
    )
    .refine(
      (data: { startTimeOfUsage?: Date | null }) => {
        if (!ats || !data.startTimeOfUsage) return true;
        const startTime = toMinute(data.startTimeOfUsage);
        return !!startTime && startTime >= ats;
      },
      {
        message: t("form-validation.start-time-after-ats"),
        path: ["startTimeOfUsage"],
      },
    );

const EditResourceDrawer: React.FC<EditResourceDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  onRemove,
  resource,
  resourceTypes,
  resources,
  showRemoveButton = true,
  validateStartAfterAts,
  ats,
  onlyResourceFieldsRequired,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const atsDate = toMinute(ats);

  const resourceTypeOptions = useMemo(
    () => mapResourceTypesToOptions(resourceTypes),
    [resourceTypes],
  );

  const form = useForm<ResourceFormData>({
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver: zodResolver(
      getResourceSchema(t, atsDate, validateStartAfterAts, onlyResourceFieldsRequired),
    ),
    shouldUnregister: true,
    defaultValues: {
      resourceType: "",
      resourceName: "",
      numberOfResources: "",
      startTimeOfUsage: null,
      endTimeOfUsage: null,
      connectionTime: null,
      location: "",
    },
  });

  const resourceType = useWatch({ control: form.control, name: "resourceType" });
  const startTimeOfUsage = useWatch({ control: form.control, name: "startTimeOfUsage" });
  const minStartTime = useMemo(() => {
    if (!atsDate) {
      return undefined;
    }

    return atsDate;
  }, [atsDate]);
  const minEndTime = useMemo(() => {
    if (!startTimeOfUsage) {
      return undefined;
    }
    return new Date(startTimeOfUsage.getTime() + 60 * 1000);
  }, [startTimeOfUsage]);

  // Get the selected resource type option for display and TUG check
  const selectedResourceTypeOption = useMemo(() => {
    return resourceTypeOptions.find((opt) => opt.value === resourceType) || null;
  }, [resourceTypeOptions, resourceType]);

  // Filter resources based on selected resource type
  const resourceNameOptions = useMemo(() => {
    if (!resourceType || !resources || resources.length === 0) {
      return [];
    }
    const filteredResources = resources.filter(
      (resource) => resource.resourceTypeCode === resourceType,
    );
    return mapResourcesToOptions(filteredResources);
  }, [resources, resourceType]);

  // Show additional fields for Tug
  const isTug =
    selectedResourceTypeOption?.value === "TUG" ||
    selectedResourceTypeOption?.label?.toUpperCase().includes("TUG");

  // Track if we're initializing to prevent clearing resourceName during init
  const isInitializingRef = React.useRef(false);
  const previousResourceTypeRef = React.useRef<string | undefined>(undefined);

  // Initialize form with resource data
  useEffect(() => {
    if (resource && isOpen && resourceTypes.length > 0) {
      isInitializingRef.current = true;
      previousResourceTypeRef.current = resource.additionalResourceType;
      form.reset({
        resourceType: resource.additionalResourceType || "",
        resourceName: resource.resourceName || "",
        numberOfResources: String(resource.numberOfResources),
        startTimeOfUsage: parseResourceDate(resource.startTimeOfUsage),
        endTimeOfUsage: parseResourceDate(resource.endTimeOfUsage),
        connectionTime: parseResourceDate(resource.connectionTime),
        location: resource.location || "",
      });
      isInitializingRef.current = false;
    }
  }, [resource, isOpen, form, resourceTypes.length]);

  const handleClose = () => {
    isInitializingRef.current = false;
    form.reset();
    onClose();
  };

  const handleRemove = () => {
    onRemove?.();
  };

  const onValid = (data: ResourceFormData) => {
    const updatedResource: ResourceInterface = {
      ...resource,
      additionalResourceType: data.resourceType,
      numberOfResources: parseInt(data.numberOfResources, 10),
    };

    if (data.startTimeOfUsage) {
      updatedResource.startTimeOfUsage = data.startTimeOfUsage.toISOString();
    } else if (resource.startTimeOfUsage !== undefined) {
      updatedResource.startTimeOfUsage = null;
    }

    if (data.endTimeOfUsage) {
      updatedResource.endTimeOfUsage = data.endTimeOfUsage.toISOString();
    } else if (resource.endTimeOfUsage !== undefined) {
      updatedResource.endTimeOfUsage = null;
    }

    // Explicitly handle resourceName
    if (data.resourceName && data.resourceName.trim()) {
      updatedResource.resourceName = data.resourceName.trim();
    } else {
      // If resourceName was cleared, set it to undefined to remove it
      updatedResource.resourceName = undefined;
    }

    if (isTug) {
      updatedResource.connectionTime = data.connectionTime?.toISOString() ?? null;
    } else if (resource.connectionTime !== undefined) {
      updatedResource.connectionTime = null;
    }

    if (isTug && data.location) {
      updatedResource.location = data.location;
    } else {
      updatedResource.location = undefined;
    }

    onSave(updatedResource);
  };

  const onSubmit = () => {
    form.handleSubmit(onValid)();
  };

  // Reset resourceName when resourceType changes (but not during initialization)
  useEffect(() => {
    if (resourceType && isOpen && !isInitializingRef.current) {
      // Only clear if resourceType actually changed (not just on mount)
      if (previousResourceTypeRef.current && previousResourceTypeRef.current !== resourceType) {
        form.setValue("resourceName", "");
      }
      previousResourceTypeRef.current = resourceType;
    }
  }, [resourceType, form, isOpen]);

  return (
    <FormProvider {...form}>
      <SideDrawer
        isOpen={isOpen}
        isChildDrawer
        title={
          <div className="flex items-center gap-2">
            <ArrowBackIcon onClick={handleClose} sx={{ cursor: "pointer" }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t("vessel-call.movement-services.close-form.edit-resource-title")}
            </Typography>
          </div>
        }
        onClose={handleClose}
        onCancel={handleClose}
        onSubmit={onSubmit}
        submitLabel={t("vessel-call.movement-services.close-form.buttons.update")}
        customFooter={
          showRemoveButton ? (
            <div
              className="flex gap-3 px-6 py-6 border-t"
              style={{ borderTopColor: theme.palette.divider }}
            >
              <Button
                variant="outlined"
                color="error"
                onClick={handleRemove}
                label={
                  <span className="normal-case font-medium">
                    {t("vessel-call.movement-services.close-form.buttons.remove-resource")}
                  </span>
                }
                testId="remove-resource-button"
                className="flex-1"
              />
              <Button
                variant="contained"
                onClick={onSubmit}
                label={
                  <span className="normal-case font-medium">
                    {t("vessel-call.movement-services.close-form.buttons.update")}
                  </span>
                }
                testId="update-resource-button"
                className="flex-1"
              />
            </div>
          ) : undefined
        }
      >
        <Box
          className="w-[600px] px-6 py-4 flex flex-col gap-4"
          sx={{
            "& > .grid > .MuiFormControl-root": {
              gridTemplateRows: "auto auto 1fr",
              alignContent: "start",
            },
          }}
        >
          {/* Resources */}
          <Box
            className="grid grid-cols-2 gap-4"
            sx={{
              "& > :last-child": { gridColumnStart: validateStartAfterAts ? 2 : 1 },
            }}
          >
            <FormItem
              name="resourceType"
              type="autocomplete"
              label={t("vessel-call.movement-services.close-form.fields.resource-type")}
              testId="resource-type-autocomplete"
              options={resourceTypeOptions}
              autocompleteValue={selectedResourceTypeOption}
            />
            {!validateStartAfterAts && (
              <FormItem
                name="resourceName"
                type="autocomplete"
                label={t("vessel-call.movement-services.close-form.fields.resource-name")}
                testId="resource-name-autocomplete"
                options={resourceNameOptions}
                autocompleteValue={
                  resourceNameOptions.find((opt) => opt.value === form.watch("resourceName")) ||
                  null
                }
              />
            )}
            <FormItem
              name="numberOfResources"
              type="number"
              label={t("vessel-call.movement-services.close-form.fields.number-of-resources")}
              testId="number-of-resources-input"
              decimals={0}
              maxDigits={4}
            />
          </Box>

          {/* Start Time and End Time */}
          <div className="grid grid-cols-2 gap-4">
            <FormItem
              name="startTimeOfUsage"
              type="date-picker"
              label={t("vessel-call.movement-services.close-form.fields.start-time")}
              testId="start-time-input"
              timePicker
              combinedInput
              minDate={minStartTime}
            />

            <FormItem
              name="endTimeOfUsage"
              type="date-picker"
              label={t("vessel-call.movement-services.close-form.fields.end-time")}
              testId="end-time-input"
              timePicker
              combinedInput
              minDate={minEndTime ?? undefined}
            />
          </div>

          {/* Additional fields for Tug */}
          {isTug && !validateStartAfterAts && (
            <div className="grid grid-cols-2 gap-4">
              <FormItem
                name="connectionTime"
                type="date-picker"
                label={t("vessel-call.movement-services.close-form.fields.connection-time")}
                testId="connection-time-input"
                timePicker
                combinedInput
              />

              <FormItem
                name="location"
                type="text"
                label={t("vessel-call.movement-services.close-form.fields.location")}
                testId="location-input"
              />
            </div>
          )}
        </Box>
      </SideDrawer>
    </FormProvider>
  );
};

export default EditResourceDrawer;
